import React, { useState } from 'react';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { CopilotIcon, AvatarIcon } from 'common/components/icons';
import ActionResultMessage from './ActionResultMessage';
import { MarkdownRenderer } from './MarkdownRenderer';

export interface Message {
  id: number | string;
  role: 0 | 1 | 2;
  message: string;
  error?: boolean;
  status?: 'connected' | 'disconnected' | 'processing_start' | 'processing_end';
  actionResult?: {
    action: string;
    success: boolean;
    navigation_url?: string;
    data?: any;
  };
}

interface CopilotMessageProps {
  message: Message;
  onFileLinkClick?: (payload: { href: string; fileId?: string; fileUrl?: string; label: string }) => void;
}

const CopilotMessage: React.FC<CopilotMessageProps> = ({ message, onFileLinkClick }) => {
  const [textCopied, setTextCopied] = useState(false);

  // Only show error UI if there's no actionResult (actual error, not a failed action)
  if (message.error && !message.actionResult) {
    return (
      <MessageWrapper $isUser={false}>
        <QuestionContainer $isUser={false}>
          <Circle>{CopilotIcon()}</Circle>
          <MessageContainer $isUser={false} $isError={true}>
            <BodyText>Error getting response</BodyText>
          </MessageContainer>
        </QuestionContainer>
      </MessageWrapper>
    );
  }

  return (
    <MessageWrapper $isUser={message.role === 1}>
      <QuestionContainer $isUser={message.role === 1}>
        <Circle>{message.role === 1 ? AvatarIcon() : CopilotIcon()}</Circle>
        <MessageContainer $isUser={message.role === 1}>
          {message.actionResult ? (
            <ActionResultMessage message={message.message} actionResult={message.actionResult} />
          ) : message.role === 0 ? (
            <MarkdownRenderer content={message.message} onFileLinkClick={onFileLinkClick} />
          ) : (
            message.message
          )}
        </MessageContainer>
        {message.role !== 1 && (
          <IconWrapper>
            <Icon
              name={textCopied ? 'check' : 'clipboard'}
              onClick={() => {
                navigator.clipboard.writeText(message.message);
                setTextCopied(true);
              }}
              color="grey"
            />
          </IconWrapper>
        )}
      </QuestionContainer>
    </MessageWrapper>
  );
};

export default CopilotMessage;

const MessageWrapper = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: ${props => (props.$isUser ? 'flex-end' : 'flex-start')};
  margin-bottom: 12px;
`;

const QuestionContainer = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: ${props => (props.$isUser ? 'row-reverse' : 'row')};
  align-items: flex-start;
  max-width: 95%;
  min-width: 0;
`;

const Circle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: #2d70e2;
  color: #ffffff;
  margin: 0 8px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MessageContainer = styled.div<{ $isUser?: boolean; $isError?: boolean }>`
  background: ${props => (props.$isUser ? '#2d70e2' : props.$isError ? '#ffe6e6' : '#f8faff')};
  color: ${props => (props.$isUser ? 'white' : props.$isError ? '#cc0000' : '#2a3039')};
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.5;
`;

const IconWrapper = styled.div`
  cursor: pointer;
  margin-left: 8px;
  display: flex;
  align-items: center;
`;

const BodyText = styled.div`
  font-size: 13px;
  color: #2a3039;
`;


